import { Component, Input, OnInit } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { PlatformEnum, UsabillaService } from '@panamax/app-state';
import { NotificationFilters } from '../../model/notification-filters.model';

@Component({
  template: '',
})
export class NotificationFiltersBaseComponent implements OnInit {
  @Input() customers: any[];
  @Input() filters: NotificationFilters;
  @Input() platform: PlatformEnum;

  alertTypes: string[];
  statuses: string[] = [];
  selectedOption: string;
  filterOptions: string[];
  searchedCustomers = [];
  localFilters: NotificationFilters;
  mobileSelectiions: NotificationFilters;

  constructor(
    private modalController: ModalController,
    private usabillaService: UsabillaService,
    protected translateService: TranslateService,
  ) {}

  ngOnInit() {
    this.alertTypes = ['Delivery', 'Order', 'Payment'];
    this.statuses = ['Unread', 'Read'];
    this.selectedOption =
      this.platform === PlatformEnum.mobile ? '' : 'customers';
    this.filterOptions = ['customers', 'alertTypes', 'statuses'];
    this.searchedCustomers = this.customers;
    if (!Capacitor.isNativePlatform()) {
      this.usabillaService.hide();
    }
    this.localFilters = {
      statuses: this.filters?.statuses ? [...this.filters?.statuses] : [],
      alertTypes: this.filters?.alertTypes ? [...this.filters?.alertTypes] : [],
      customers: this.filters?.customers ? [...this.filters?.customers] : [],
    };
  }

  setSelectedOption(option: string) {
    this.selectedOption = option;
    this.mobileSelectiions = {
      statuses: [...this.localFilters?.statuses],
      alertTypes: [...this.localFilters?.alertTypes],
      customers: [...this.localFilters?.customers],
    };
  }

  clearSelectedOption() {
    this.selectedOption = '';
  }

  searchCustomer(event: any) {
    const value = event?.target?.value || '';
    this.searchedCustomers =
      this.customers?.filter(
        c =>
          !value || // no filtering if input is empty
          c.customerName.toLowerCase().includes(value.toLowerCase()) || // of filter by customer name
          c.customerNumber.toString().includes(value), // of filter by customer number
      ) || []; // return empty array if there is no customers in filters
  }

  clearCustomerSearch() {
    this.searchedCustomers = this.customers || [];
  }

  updateCustomersSelected(customerNumber: number) {
    if (this.platform !== PlatformEnum.mobile) {
      if (this.localFilters?.customers.indexOf(customerNumber.toString()) < 0) {
        this.localFilters.customers.push(customerNumber.toString());
      } else {
        this.localFilters?.customers.splice(
          this.localFilters?.customers.indexOf(customerNumber.toString()),
          1,
        );
      }
    } else {
      if (
        this.mobileSelectiions?.customers.indexOf(customerNumber.toString()) < 0
      ) {
        this.mobileSelectiions.customers.push(customerNumber.toString());
      } else {
        this.mobileSelectiions?.customers.splice(
          this.mobileSelectiions?.customers.indexOf(customerNumber.toString()),
          1,
        );
      }
    }
  }

  updateAlertTypesSelected(alertType: string) {
    if (this.platform !== PlatformEnum.mobile) {
      if (this.localFilters?.alertTypes.indexOf(alertType.toLowerCase()) < 0) {
        this.localFilters?.alertTypes.push(alertType.toLowerCase());
      } else {
        this.localFilters.alertTypes.splice(
          this.localFilters?.alertTypes.indexOf(alertType.toLowerCase()),
          1,
        );
      }
    } else {
      if (
        this.mobileSelectiions?.alertTypes.indexOf(alertType.toLowerCase()) < 0
      ) {
        this.mobileSelectiions?.alertTypes.push(alertType.toLowerCase());
      } else {
        this.mobileSelectiions.alertTypes.splice(
          this.mobileSelectiions?.alertTypes.indexOf(alertType.toLowerCase()),
          1,
        );
      }
    }
  }

  updateStatusSelected(status: boolean) {
    if (this.platform !== PlatformEnum.mobile) {
      if (this.localFilters?.statuses.indexOf(status) < 0) {
        this.localFilters?.statuses.push(status);
      } else {
        this.localFilters.statuses.splice(
          this.localFilters?.statuses.indexOf(status),
          1,
        );
      }
    } else {
      if (this.mobileSelectiions?.statuses.indexOf(status) < 0) {
        this.mobileSelectiions?.statuses.push(status);
      } else {
        this.mobileSelectiions.statuses.splice(
          this.mobileSelectiions?.statuses.indexOf(status),
          1,
        );
      }
    }
  }

  isCustomerChecked(customerNumber: number) {
    return (
      this.localFilters?.customers.indexOf(customerNumber.toString()) > -1 ||
      this.mobileSelectiions?.customers.indexOf(customerNumber.toString()) > -1
    );
  }

  isStatusChecked(status: boolean) {
    return (
      this.localFilters?.statuses.indexOf(status) > -1 ||
      this.mobileSelectiions?.statuses.indexOf(status) > -1
    );
  }

  isAlertTypeChecked(alertType: string) {
    return (
      this.localFilters?.alertTypes.indexOf(alertType.toLowerCase()) > -1 ||
      this.mobileSelectiions?.alertTypes.indexOf(alertType.toLowerCase()) > -1
    );
  }

  saveMobileFilters() {
    this.localFilters = {
      statuses: [...this.mobileSelectiions.statuses],
      alertTypes: [...this.mobileSelectiions.alertTypes],
      customers: [...this.mobileSelectiions.customers],
    };
    this.selectedOption = '';
  }

  get totalFiltersSelected(): number {
    return (
      this.localFilters?.alertTypes?.length +
      this.localFilters?.statuses?.length +
      this.localFilters?.customers?.length
    );
  }

  async clearAll() {
    await this.modalController.dismiss({
      filters: null,
    });
  }

  async apply() {
    await this.modalController.dismiss({
      filters: this.localFilters,
    });
  }

  async dismiss() {
    await this.modalController.dismiss();
  }
}
